'use client';

import { useQueryClient } from '@tanstack/react-query';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { capture } from '@/lib/analytics/posthog';
import { mergeVisitorAfterLogin, resetVisitorMerge } from '@/lib/api/analytics/client';
import { logout as apiLogout, probeSession, updateUserLocale } from '@/lib/api/auth/client';
import type { UserProfile } from '@/lib/api/auth/models';
import { USER_PROFILE_QK } from '@/lib/api/auth/queryKeys';
import { configureHttpAuth } from '@/lib/api/httpClient';
import { readStoredActivityId } from '@/lib/auth/responseMapper';
import { hasSessionEvidence, rememberSession } from '@/lib/auth/sessionEvidence';
import { clearReferralCode } from '@/lib/utils/referralCode';
import { clearResendCooldown } from '@/lib/utils/resendCooldown';

type AuthUser = {
  id: string;
  email: string;
  name?: string;
  confirmed?: boolean;
  userGroup?: string;
  activityId?: string;
  twoFactorEnabled?: boolean;
  language?: string;
};

type AuthContextValue = {
  user: AuthUser | null;
  /** @deprecated Always null — auth is cookie-based. Kept for backward compat; remove in Task 7. */
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  setAuthData: (params: { user: AuthUser; token?: string }) => void;
  clearAuthData: () => void;
  /**
   * Clears local session state and localStorage without making a server logout
   * request. Use when the caller has already awaited the server logout (e.g.
   * useSignOut mutationFn) to avoid a redundant DELETE /auth/logout.
   */
  clearLocalSession: () => void;
};

export const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/** Maps the /api/user/me response to the AuthUser shape. */
function mapMeToAuthUser(me: UserProfile): AuthUser {
  return {
    id: me.id ?? '',
    email: me.email ?? '',
    name: me.nickname,
    confirmed: me.confirmed,
    userGroup: me.userGroup,
    activityId: me.activityId ?? readStoredActivityId(),
    twoFactorEnabled: me.twoFactorEnabled,
    // locale arrives as uppercase (e.g. 'EN') — normalise to lowercase for next-intl
    language: me.locale ? me.locale.toLowerCase() : undefined,
  };
}

// These are pure helpers with no closure over component state — defined at module
// level so useCallback[] deps list stays empty.
function setLocaleCookie(locale: string) {
  try {
    const isSecure = typeof location !== 'undefined' && location.protocol === 'https:';
    const maxAge = 60 * 60 * 24 * 365; // 1 year
    document.cookie = `outpoll_user_locale=${encodeURIComponent(locale)}; Path=/; Max-Age=${maxAge}; SameSite=Lax${isSecure ? '; Secure' : ''}`;
  } catch {}
}

function getLocaleCookie(): string | null {
  try {
    const match = document.cookie.match(/outpoll_user_locale=([^;]+)/);
    return match?.[1]?.trim() ? decodeURIComponent(match[1].trim()) : null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const onLogout = () => {
      setUser(null);
      rememberSession(false);
      if (typeof window !== 'undefined') window.location.href = '/auth/login';
    };

    configureHttpAuth({
      refreshUrl: `${process.env.NEXT_PUBLIC_API_AUTH_URL}/auth/refresh`,
      onLogout,
    });

    // Most visitors are anonymous, and for them the boot probe is a guaranteed
    // 401 on every page load. Skip it unless this browser has shown any sign
    // of a session; a wrong "anonymous" verdict only costs a login prompt the
    // visitor would have seen anyway.
    if (!hasSessionEvidence()) {
      setUser(null);
      setIsLoading(false);
      return;
    }

    probeSession()
      .then((me) => {
        // The probe is the same `GET /api/user/me` the header then reads as
        // ['userProfile'] — hand the answer over before the session flips, so
        // the header finds it instead of asking a second time.
        queryClient.setQueryData(USER_PROFILE_QK, me);
        setUser(mapMeToAuthUser(me));
        rememberSession(true);
      })
      .catch(() => {
        setUser(null);
        rememberSession(false);
      })
      .finally(() => setIsLoading(false));
  }, [queryClient]);

  // `login_completed` once per session, like the visitor merge below: setAuthData
  // is also called on password change and 2FA step-up for an existing session.
  const hasTrackedLoginRef = useRef(false);

  const setAuthData = useCallback((params: { user: AuthUser; token?: string }) => {
    // Respect locale chosen on auth page: cookie takes priority over backend language
    const cookieLocale = getLocaleCookie();
    const backendLanguage = params.user.language;
    const userToStore =
      cookieLocale && cookieLocale !== backendLanguage
        ? { ...params.user, language: cookieLocale }
        : params.user;

    setUser(userToStore);
    rememberSession(true);

    // Attach any anonymous analytics history (visitor_id) to this account.
    // Idempotent + runs once per login; resetVisitorMerge() re-arms it on logout.
    mergeVisitorAfterLogin();

    if (!hasTrackedLoginRef.current) {
      hasTrackedLoginRef.current = true;
      capture('login_completed', {
        confirmed: params.user.confirmed,
        two_factor_enabled: params.user.twoFactorEnabled,
      });
    }

    // The referral code (if any) has served its purpose once a session is
    // established — clear it so it can't pre-fill or re-attach to later flows.
    clearReferralCode();

    // Set locale cookie: use cookie locale if it exists, otherwise backend language
    if (cookieLocale) {
      // Cookie already set — keep it; sync backend if different
      if (cookieLocale !== backendLanguage) {
        updateUserLocale(cookieLocale).catch(() => {});
      }
    } else if (backendLanguage) {
      setLocaleCookie(backendLanguage);
    }
  }, []);

  /** Clears local session state and localStorage (no network call). */
  const clearLocalSession = useCallback(() => {
    setUser(null);
    rememberSession(false);
    // Clear non-auth-token localStorage items; preserve referral code
    try {
      // Auth-related keys, minus referralCode
      const keysToRemove = [
        'pendingVerificationEmail',
        'pendingVerificationSource',
        'pendingForAuthUrl',
        'outpoll_change_password_step1',
        'reset_password_email',
      ];
      keysToRemove.forEach((key) => localStorage.removeItem(key));
      clearResendCooldown();
    } catch {}
    resetVisitorMerge();
    hasTrackedLoginRef.current = false;
  }, []);

  const clearAuthData = useCallback(() => {
    // Call the server-side logout (deletes HttpOnly cookies) then reset local state
    apiLogout().catch(() => {});
    clearLocalSession();
  }, [clearLocalSession]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      // token is always null — auth is cookie-based. Kept in context for backward
      // compat with consumers that haven't been migrated yet (Task 7 will clean up).
      token: null,
      isAuthenticated: Boolean(user),
      isLoading,
      setAuthData,
      clearAuthData,
      clearLocalSession,
    }),
    [user, isLoading, setAuthData, clearAuthData, clearLocalSession],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthContext(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuthContext must be used within AuthProvider');
  return ctx;
}
